import React, { useState, useEffect } from 'react';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Body from '../Components/layout/Body';
import { getBodySiteBtnsForValues } from './ConstBodySiteBtns';
import { BodySiteBtnsView } from './BodySiteBtnsView';
import TestDriveSetupSidebar from './TestDriveSetupSidebar';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_SETUP_BODY_HELP = `
  <div>
    <p>Here you’ll choose how exactly the client will control the access method during this Test-drive.  
    First choose the body site (or when applicable, more than one body site). 
    Then make any comments about how the client is going to use that body site.  
    <strong>For example</strong>, a client might be using a trackball with their right hand.  
    But they are going to use the back of hand, with the forearm supinated, so you could make a note of that in the comments. 
    </p>
    <p>
    <strong>Note:</strong> If you have specified particular body sites already, in the Evaluate Abilities section, you will initially see only those body sites as options here.  
    This is just to try to make your life simpler.  
    If you’d rather see all the body sites, just click ‘show all sites.’ 
    </p>
  </div>
`

const propTypes = {
  bodySites: PropTypes.arrayOf(PropTypes.string), //from Eval Abilities
  activeAccessMethodName: PropTypes.string,
  activeAccessMethodCategory: PropTypes.string,
  activeSetupSelectionMethod: PropTypes.string,
  activeSetupAccessories: PropTypes.arrayOf(PropTypes.string),
  activeSetupName: PropTypes.string,
  activeSetupBodySite: PropTypes.arrayOf(PropTypes.string),
  onChangeActiveSetupBodySite: PropTypes.func.isRequired,
  activeSetupBodySiteComments: PropTypes.string,
  onChangeActiveSetupBodySiteComments: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  bodySites: [],
  activeAccessMethodName: '',
  activeAccessMethodCategory: '',
  activeSetupSelectionMethod: '',
  activeSetupBodySite: [],
  activeSetupBodySiteComments: '',
  activeSetupAccessories: [],
  activeSetupName: '',
};

//content sub-component with unique content for this page
function TestDriveSetupBodySitesContent(props) {
  //Render the form for asking about setup info.
  //Calls the setter that's passed in by the parent, when a radio button is clicked.
  //That setter changes the state of the vars in the parent, 
  //which triggers a new render of the view with the new var values.
    //props for customizing view with client info
    const {
      bodySites,
      //these can be changed:
      activeSetupBodySite,
      onChangeActiveSetupBodySite,
      activeSetupBodySiteComments,
      onChangeActiveSetupBodySiteComments
    } = props;

    //true if we've chosen any bodySites outside of the ones from Evaluate Abilities
    const anyExtraBodySites = !_.isEmpty(_.difference(activeSetupBodySite, bodySites));
    const noPreselectedBodySites =  _.isEmpty(bodySites);
    const noSubsetForm = anyExtraBodySites || noPreselectedBodySites;

    const [showAll, setShowAll] = useState(false || noSubsetForm);

    const bodySiteOptionBtns = getBodySiteBtnsForValues(bodySites);
    return(
    <>
    <Form>
      { !showAll ?
      <FormGroup tag="fieldset">
        <legend>Choose from the priority body sites you picked earlier:
          <Button color="link" style={{float:'right'}} onClick={() => setShowAll(true)}>show all sites</Button>
        </legend>
        {bodySiteOptionBtns.map((btn, index) =>
          <FormGroup check key={index}>
            <Input
              id={btn.value}
              value={btn.value}
              checked={activeSetupBodySite.indexOf(btn.value) > -1}
              onChange={onChangeActiveSetupBodySite} 
              name="environment"
              type="checkbox"
            />
            {' '}
            <Label for={btn.value} check>
              {btn.label}
              {' '}
              <span className="aa-more-info">{btn.moreInfo}</span>
            </Label>
          </FormGroup>
        )}
      </FormGroup>
      : null
      }    
      {/* { !showForm ? 
      <Button onClick={() => setShowAll(!showAll)}>Toggle All Body-sites</Button>
      : null  
      }*/}
      { showAll ?
        <FormGroup tag="fieldset">
          <legend>Choose from all the body sites: 
            { !noSubsetForm ?
              <Button color="link" style={{float:'right'}} onClick={() => setShowAll(false)}>hide</Button>
            : null
            }
          </legend>
          <BodySiteBtnsView
            bodySites={activeSetupBodySite}
            onChangeBodySites={onChangeActiveSetupBodySite}
          />
        </FormGroup>
      : null
      }
      <FormGroup>
        <Label for="body-site-comments" className="aa-bold-text">
          Enter any comments about the body sites:
        </Label>
        <Input
          id="body-site-comments"
          value={activeSetupBodySiteComments}
          onChange={onChangeActiveSetupBodySiteComments}
          name="body-site-comments"
          type="textfield"
        />
      </FormGroup>
    </Form>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveSetupBodySites(props) {
  //props for customizing views with client info
  const firstName = props.firstName;
  const bodySites = props.bodySites;
  //props for setup info section
  const activeAccessMethodName = props.activeAccessMethodName;
  const activeSetupSelectionMethod = props.activeSetupSelectionMethod;
  const activeSetupAccessories = props.activeSetupAccessories;
  const activeSetupName = props.activeSetupName;
  const activeSetupBodySite = props.activeSetupBodySite;
  const onChangeActiveSetupBodySite = props.onChangeActiveSetupBodySite;
  const activeSetupBodySiteComments = props.activeSetupBodySiteComments;
  const onChangeActiveSetupBodySiteComments = props.onChangeActiveSetupBodySiteComments;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;
  const dev = props.dev || {};

  console.log("rendering TestDriveSetupBodySites, with props: " + JSON.stringify(props));
  const title = `What body site(s) will ${firstName} use to control this access method?`;
  const pageData = {
    image: "", //not yet used
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: title,
    subTitle: "",
    learnMore: {linkText: "Learn More about these body site options",
                title: "Body site options",
                content: TD_SETUP_BODY_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('TestDriveSetupBodySites mounted');
    return () => {
      console.log('TestDriveSetupBodySites unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveSetupBodySitesContent 
          bodySites={bodySites}
          activeAccessMethodName={activeAccessMethodName}
          activeSetupBodySite={activeSetupBodySite}
          onChangeActiveSetupBodySite={onChangeActiveSetupBodySite}
          activeSetupBodySiteComments={activeSetupBodySiteComments}
          onChangeActiveSetupBodySiteComments={onChangeActiveSetupBodySiteComments}
        />
      }
      sidebarComponent={
        dev.showDevSidebar 
        ?
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
        :  
        <TestDriveSetupSidebar 
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={activeSetupSelectionMethod}
          activeSetupBodySite={activeSetupBodySite}
          activeSetupAccessories={activeSetupAccessories}
          activeSetupName={activeSetupName}
       />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

TestDriveSetupBodySites.propTypes = propTypes;
TestDriveSetupBodySites.defaultProps = defaultProps;

export default TestDriveSetupBodySites;